import './style.scss';

import * as Toast from '@/utils/Toast';

import { GeneralButton, NegativeOption } from '@/interfaces';
import { NEGATIVE_OPTION_TYPE, TRACKING_MSG_TYPE, TRACKING_TYPE } from '@/utils/constants';
import { handleRequest } from '@/utils';
import { addDialogue, updateDialogueInfo } from '@/utils/sessionUtils';
import { classnames, getContent, handleClickHistoryButton, throttle, getTrans } from '@/utils/tools';
import { inject, observer } from 'mobx-react';

import General from '@/components/General';
import Msg from '@/components/Msg';
import React from 'react';
import { TrackerProps } from '@/types/common';
import clsx from 'classnames';
import { tracker } from '@/utils/tracking';
import { getChildInject, bridgeCallFunc } from '@/utils/instantLoaded/tools';
import { isBank } from '@/config/core';
import GeneralTextArea from '../Common/GeneralTextArea';

type Props = {
  msgContent: string;
  options: NegativeOption[];
  buttons: GeneralButton[];
  historyDialogueId: string,
  historySessionId: string,

  nodePointId: string;
  dialogueId: string;
  dialogueType: string;
  listPos: number;
  isDisabled: boolean;
  disabledBtn: boolean;
  isHistory: boolean;

  initStore: any;
} & TrackerProps;

interface State {
  chooseOption?: NegativeOption;
  freetext: string;
}

const PREFIX = 'feedback-options-container';

const MAX_FREETEXT = 500;

@inject(getChildInject('initStore'))
@observer
class NegativeOptions extends React.PureComponent<Props, State> {
  static defaultProps = {
    msgContent: '',
    options: [],
    buttons: [],
    dialogueId: '',
    dialogueType: '',
    isDisabled: false,

    initStore: {},
  };

  trackingname = TRACKING_TYPE.STANDARDIZE_BUTTON;
  trackingdata = {};

  constructor(props: Props) {
    super(props);

    const { initStore, dialogueId, nodePointId, isHistory, historyDialogueId, historySessionId } = this.props;

    this.state = {
      chooseOption: undefined,
      freetext: '',
    };

    this.trackingdata = {
      chat_session_id: isHistory ? historySessionId : (initStore.sessionId || ''),
      chat_dialogue_id: isHistory ? historyDialogueId : dialogueId,
      node_point_id: nodePointId,
      if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
    };
  }

  getButtonTracking = (button: any) => {
    return {
      button_id: button.button_id || '',
      button_type: button.button_type || '',
      button_text: button.text || '',
    };
  };

  updateState(name: string | Record<string, any>, value?: any) {
    if (typeof name === 'string') {
      this.setState({
        ...this.state,
        [name]: value,
      });
    } else {
      this.setState({
        ...this.state,
        ...name,
      });
    }
  }

  showToast = throttle(() => {
    bridgeCallFunc(() => {
      Toast.error(getTrans('feedback_option_freetext_exceed'));
    });
  }, 2000, false);

  handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    let newValue = '';

    if (value.length > MAX_FREETEXT) {
      newValue = value.slice(0, MAX_FREETEXT);

      this.showToast();
    } else {
      newValue = value;
    }
    this.updateState('freetext', newValue);
  };

  handleTextAreaChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    this.updateState('freetext', value);
  };

  handleInputFocus = () => {
    const { initStore } = this.props;
    initStore.UPDATE_BOTTOMBAR_HIDDEN(true);
  };

  handleInputBlur = () => {
    const { initStore } = this.props;
    setTimeout(() => {
      initStore.UPDATE_BOTTOMBAR_HIDDEN(false);
    }, 100); // 延迟恢复bottom bar，给点击submit按钮留下足够时间触发
  };

  handleSubmit = async (button: any) => {
    const {
      chooseOption,
      freetext,
    } = this.state;
    const { isHistory } = this.props;

    const trimFreetext = freetext.trim();

    this.props.tracker.click({
      trackingname: this.trackingname,
      data: {
        ...this.trackingdata,
        ...this.getButtonTracking(button),
        button_text: getContent(button),
      },
    });

    if (!chooseOption && !isHistory) {
      bridgeCallFunc(() => {
        Toast.error(getTrans('feedback_option_required'));
      });
      return;
    }

    if (!chooseOption) {
      return;
    }

    if (isHistory) {
      handleClickHistoryButton();
      return;
    }

    const {
      dialogueId,
      dialogueType,
      listPos,
      initStore,
    } = this.props;

    const { sessionId } = initStore;

    let userContent = '';
    switch (chooseOption.option_type) {
      case NEGATIVE_OPTION_TYPE.CLICK:
        userContent = chooseOption.text;
        break;
      case NEGATIVE_OPTION_TYPE.FREETEXT:
        userContent = chooseOption.text + (trimFreetext ? `: ${trimFreetext}` : '');
        break;
    }

    addDialogue({
      dialogueId: dialogueId,
      dialogueType: dialogueType,
      from: 'user',
      type: 'Msg',
      data: { content: userContent },
    }, false);

    handleRequest('postNegativeOptions', {
      dialogue_id: dialogueId,
      // feedback: freetext,
      option_id: chooseOption.id,
      option_text: userContent,
      session_id: sessionId,
    }).then((res: any) => {
      // 隐藏选项
      res && listPos && updateDialogueInfo({ isDisabled: true }, listPos);
    });
  };

  trackingHandler = (option: NegativeOption) => {
    this.props.tracker.click({
      trackingname: this.trackingname,
      data: {
        ...this.trackingdata,
        ...this.getButtonTracking(option),
      },
    });
  };

  generateOption = (option: NegativeOption, index: number) => {
    const {
      chooseOption,
      freetext,
    } = this.state;
    const { disabledBtn, isHistory } = this.props;
    const isChoosen = chooseOption && chooseOption.id === option.id;

    return (<div key={`option-${index}`}>
      <label className={
        classnames('option-radio-wrapper', {
          'option-radio-wrapper-checked': isChoosen,
          'no-bottom-line': option.option_type === NEGATIVE_OPTION_TYPE.FREETEXT && (isChoosen || disabledBtn),
        })
      }>
        <span className={clsx('radio', { disabled: disabledBtn })}>
          <input
            type="radio"
            className="radio-input"
            value={option.id}
            checked={isChoosen}
            onClick={() => {
              this.trackingHandler(option);
              if (isHistory) {
                return;
              }
              this.updateState({ chooseOption: option, freetext: '' });
            }}
          />
          <span className="radio-inner"></span>
        </span>
        <span className="content">{option.text}</span>
      </label>
      {
        // 在历史消息中会展示出来
        option.option_type === NEGATIVE_OPTION_TYPE.FREETEXT && (isChoosen || disabledBtn)
          ? (isBank()
            ? <GeneralTextArea
              disabled={disabledBtn}
              className={clsx('freetext-textarea', { disabled: disabledBtn })}
              onChange={this.handleTextAreaChange}
              maxLength={100}
            />
            : <input
              disabled={disabledBtn}
              className={clsx('freetext-input', { disabled: disabledBtn })}
              type="text"
              placeholder={getTrans('feedback_option_freetext_placeholder')}
              value={freetext}
              onChange={this.handleInputChange}
              onFocus={this.handleInputFocus}
              onBlur={this.handleInputBlur}
            />)
          : null
      }
    </div>);
  };

  render() {
    const {
      msgContent,
      options,
      buttons,
      isDisabled,
      isHistory,

      dialogueId,
      dialogueType,
      listPos,
      disabledBtn,
      historyDialogueId,
      historySessionId,

      passedRef,
      nodePointId,
    } = this.props;

    const { chooseOption } = this.state;

    const submitDisabled = !chooseOption;

    const generalProps = {
      nodePointId,
      dialogueId,
      dialogueType,
      chatEntry: 'FEEDBACK',
      listPos,
      isHistory,
      historyDialogueId,
      historySessionId,
      options: buttons.map(button => {
        const { button_type } = button;
        return {
          ...button,
          disabledBtn,
          isDisabledStyle: button_type === 'btn_feedback' && submitDisabled,
          onClick: button_type === 'btn_feedback' ? () => this.handleSubmit(button) : null,
        };
      }),
    };

    const trackingdata = JSON.stringify({
      ...this.trackingdata,
      button_details: options.map(option => ({ ...this.getButtonTracking(option) })),
    });

    return (
      <div
        className={PREFIX}
        ref={passedRef}
        data-trackingname={this.trackingname}
        data-trackingdata={trackingdata}
      >
        <Msg content={msgContent} />
        {
          !isDisabled
            ? <>
              <div className="feedback-options">
                {
                  options.map((option, index) => {
                    return this.generateOption(option, index);
                  })
                }
              </div>
              <General {...generalProps} />
            </>
            : null
        }
      </div>
    );
  }
}

export default tracker(NegativeOptions);
